import { useState, useRef, useCallback, useEffect } from 'react';

interface UseDesktopNavHoverProps {
  isHoverMode: boolean;
  expanded: boolean;
  isCrawler: boolean;
  closeOnOutsideClick?: boolean;
}

export const useDesktopNavHover = ({ 
  isHoverMode, 
  expanded, 
  isCrawler,
  closeOnOutsideClick = true, 
}: UseDesktopNavHoverProps) => {
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  
  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleItemMouseEnter = useCallback((itemId: string) => {
    if (!isHoverMode || expanded) return;
    clearHoverTimeout();
    setHoveredItemId(itemId);
  }, [clearHoverTimeout, isHoverMode, expanded]);

  const handleItemClick = useCallback((itemId: string) => {
    if (isHoverMode || expanded) return;
    clearHoverTimeout();
    setHoveredItemId(prev => prev === itemId ? null : itemId);
  }, [clearHoverTimeout, isHoverMode, expanded]);

  const handleNavLeave = useCallback(() => {
    if (!isHoverMode || expanded) return;
    clearHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItemId(null);
    }, 150);
  }, [isHoverMode, expanded, clearHoverTimeout]);

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!closeOnOutsideClick || expanded) return;
    
    // Check if click is outside the navigation menu
    if (navRef.current && !navRef.current.contains(event.target as Node)) {
      setHoveredItemId(null);
    }
  }, [closeOnOutsideClick, expanded]);

  useEffect(() => {
    if (!closeOnOutsideClick) return;

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [closeOnOutsideClick, handleOutsideClick]);

  const isItemExpanded = useCallback((itemId: string) => {
    return expanded || hoveredItemId === itemId;
  }, [expanded, hoveredItemId]);


  // For crawlers, return simplified no-op functions
  if (isCrawler) {
    return {
      hoveredItemId: null,
      handleItemMouseEnter: () => undefined,
      handleItemClick: () => undefined,
      handleNavLeave: () => undefined,
      isItemExpanded: () => false,
      navRef: { current: null },
    };
  }


  return {
    hoveredItemId,
    handleItemMouseEnter,
    handleItemClick,
    handleNavLeave,
    isItemExpanded,
    navRef,
  };
}; 